import { fetchMailMessage } from "@/lib/api/imap";
import {
  downloadMailAttachmentToDisk,
  saveMailAttachmentToCoffre,
  type MailAttachmentLocation,
} from "@/lib/mail/saveMailAttachmentToSuite";
import type { CoffreListZone } from "@/lib/suite/coffreSuiteApi";
import type { MailAttachment, MailMessageSummary } from "@/lib/types/mail";

export type MailAttachmentBulkTarget = {
  accountId: number;
  folder: string;
  uid: number;
  subject: string;
};

export type ResolvedMailAttachment = MailAttachmentBulkTarget & {
  attachments: MailAttachment[];
};

export type BulkAttachmentProgress = {
  done: number;
  total: number;
  label?: string;
};

const MESSAGE_FETCH_CONCURRENCY = 3;

export function filterMessagesWithAttachments(
  messages: MailMessageSummary[],
  keys: { uid: number; accountId?: number }[],
  fallbackAccountId?: number,
  resolveFolder?: (uid: number, accountId: number) => string
): MailAttachmentBulkTarget[] {
  const out: MailAttachmentBulkTarget[] = [];
  for (const key of keys) {
    if (key.uid <= 0) continue;

    const msg =
      key.accountId != null
        ? messages.find((m) => m.uid === key.uid && m.accountId === key.accountId) ??
          messages.find((m) => m.uid === key.uid)
        : messages.find((m) => m.uid === key.uid);

    const accountId = msg?.accountId ?? key.accountId ?? fallbackAccountId;
    if (accountId == null) continue;

    // Ne pas exclure si le flag n'est pas encore connu (undefined) — vérification IMAP ensuite.
    if (msg?.hasAttachments === false) continue;

    out.push({
      accountId,
      folder: msg?.imapFolder ?? resolveFolder?.(key.uid, accountId) ?? "INBOX",
      uid: key.uid,
      subject: msg?.subject ?? "",
    });
  }
  return out;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      results[index] = await fn(items[index], index);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export async function resolveAttachmentsForTargets(
  targets: MailAttachmentBulkTarget[],
  resolveFolder?: (uid: number, accountId: number) => string
): Promise<ResolvedMailAttachment[]> {
  const resolved = await mapWithConcurrency(targets, MESSAGE_FETCH_CONCURRENCY, async (target) => {
    const folder = resolveFolder?.(target.uid, target.accountId) ?? target.folder;
    const res = await fetchMailMessage({
      accountId: target.accountId,
      folder,
      uid: target.uid,
    });
    const attachments = res.ok ? (res.data.message?.attachments ?? []) : [];
    return {
      ...target,
      folder,
      attachments: attachments.filter((file) => file.part && file.filename),
    };
  });

  return resolved.filter((entry) => entry.attachments.length > 0);
}

function locationFromTarget(target: MailAttachmentBulkTarget): MailAttachmentLocation {
  return {
    accountId: target.accountId,
    folder: target.folder,
    messageUid: target.uid,
  };
}

export async function bulkDownloadAttachments(
  targets: MailAttachmentBulkTarget[],
  resolveFolder?: (uid: number, accountId: number) => string,
  onProgress?: (progress: BulkAttachmentProgress) => void
): Promise<{ ok: number; failed: number; skipped: number }> {
  const resolved = await resolveAttachmentsForTargets(targets, resolveFolder);
  let total = 0;
  for (const entry of resolved) total += entry.attachments.length;
  if (total === 0) return { ok: 0, failed: 0, skipped: targets.length };

  let done = 0;
  let ok = 0;
  let failed = 0;

  for (const entry of resolved) {
    for (const attachment of entry.attachments) {
      onProgress?.({ done, total, label: attachment.filename });
      const result = await downloadMailAttachmentToDisk(locationFromTarget(entry), attachment);
      if (result.ok) ok += 1;
      else failed += 1;
      done += 1;
      onProgress?.({ done, total, label: attachment.filename });
      await new Promise((r) => window.setTimeout(r, 120));
    }
  }

  return { ok, failed, skipped: targets.length - resolved.length };
}

export async function bulkSaveAttachmentsToCoffre(
  targets: MailAttachmentBulkTarget[],
  destPath: string,
  zone: CoffreListZone = "vault",
  resolveFolder?: (uid: number, accountId: number) => string,
  onProgress?: (progress: BulkAttachmentProgress) => void
): Promise<{ ok: number; failed: number; skipped: number }> {
  const resolved = await resolveAttachmentsForTargets(targets, resolveFolder);
  let total = 0;
  for (const entry of resolved) total += entry.attachments.length;
  if (total === 0) return { ok: 0, failed: 0, skipped: targets.length };

  let done = 0;
  let ok = 0;
  let failed = 0;

  for (const entry of resolved) {
    for (const attachment of entry.attachments) {
      onProgress?.({ done, total, label: attachment.filename });
      const result = await saveMailAttachmentToCoffre(
        locationFromTarget(entry),
        attachment,
        destPath,
        zone
      );
      if (result.ok) ok += 1;
      else failed += 1;
      done += 1;
      onProgress?.({ done, total, label: attachment.filename });
    }
  }

  return { ok, failed, skipped: targets.length - resolved.length };
}
